import React, { useEffect, useState } from 'react';

function formatWorked(ms) {
  const total = Math.max(0, Math.round(Number(ms) / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function writingTail(text) {
  const raw = String(text || '').replace(/\r/g, '').trim();
  if (!raw) return '';
  const lines = raw.split('\n').slice(-7);
  return lines.join('\n').slice(-240);
}

function LiveScreenThumb({ writing, active = false, onOpen }) {
  const text = writingTail(writing?.text) || String(writing?.note || '').trim();
  const writingNow = Boolean(writing?.writing && text);
  return (
    <button
      type="button"
      className="chat-live-thumb"
      aria-label="Open the computer"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof onOpen === 'function') onOpen();
      }}
    >
      <span className="chat-live-thumb-screen is-monitor" aria-hidden="true">
        <span className="chat-live-thumb-monitor">
          <span className="chat-live-thumb-monitor-screen">
            {writingNow ? (
              <span className="chat-live-thumb-code">
                {text}
                {active ? <span className="chat-live-thumb-caret" /> : null}
              </span>
            ) : null}
          </span>
          <span className="chat-live-thumb-monitor-neck" />
          <span className="chat-live-thumb-monitor-base" />
        </span>
      </span>
    </button>
  );
}

function useWorkPhrase(active, startedAt, workedMs) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active, startedAt]);

  const elapsed = active && startedAt ? Math.max(0, now - startedAt) : workedMs;
  return active
    ? (startedAt ? `Working for ${formatWorked(elapsed)}` : 'Working')
    : (elapsed != null ? `Worked for ${formatWorked(elapsed)}` : 'Worked');
}

export function ComposerActivityDock({
  active = false,
  steps = [],
  liveWriting = null,
  onOpenComputer,
}) {
  const [open, setOpen] = useState(false);
  const phrase = active ? 'Working' : 'Task completed';
  const list = Array.isArray(steps) ? steps.map((step) => String(step || '').trim()).filter(Boolean) : [];
  const canOpen = list.length > 0;

  return (
    <div className="composer-live-dock">
      <LiveScreenThumb writing={liveWriting} active={active} onOpen={onOpenComputer} />
      <div className="composer-live-status-col">
        {open && canOpen ? (
          <ul className="chat-activity-steps composer-live-steps">
            {list.map((step, index) => (
              <li key={`${index}-${step}`}>{step}</li>
            ))}
          </ul>
        ) : null}
        <div
          className={`chat-activity composer-live-status${active ? ' is-live' : ' is-settled'}`}
          aria-live="polite"
          aria-atomic="true"
          aria-busy={active}
        >
          <span className="chat-activity-mark" aria-hidden="true" />
          <button
            type="button"
            className="chat-activity-phrase"
            aria-expanded={canOpen ? open : undefined}
            disabled={!canOpen}
            onClick={() => {
              if (canOpen) setOpen((value) => !value);
            }}
          >
            <span>{phrase}</span>
            {canOpen ? <span className={`chat-activity-chevron${open ? ' is-open' : ''}`} aria-hidden="true" /> : null}
          </button>
        </div>
      </div>
    </div>
  );
}

export function liveWritingFromBuild(siteBuild, steps) {
  const phase = siteBuild?.phase || '';
  const files = Array.isArray(siteBuild?.files) ? siteBuild.files : [];
  const activeFile = files.find((file) => file.path === siteBuild?.active) || files[files.length - 1];
  const lastStep = Array.isArray(steps) ? String(steps[steps.length - 1] || '').trim() : '';
  const fileText = String(activeFile?.shown || '').trim()
    || (phase === 'done' ? String(activeFile?.content || '').trim() : '');
  const writingFiles = (phase === 'code' || phase === 'done') && Boolean(fileText);
  if (writingFiles && activeFile) {
    return {
      title: activeFile.path || 'Writing',
      text: activeFile.shown || activeFile.content || '',
      note: siteBuild?.fileNote || lastStep,
      writing: true,
    };
  }
  return {
    title: 'Computer',
    text: '',
    note: lastStep,
    writing: false,
  };
}

export default function ChatActivityStatus({
  active = false,
  label = '',
  startedAt = 0,
  workedMs = null,
  steps = [],
  statusOnComposer = false,
}) {
  const [open, setOpen] = useState(false);
  const phrase = useWorkPhrase(active, startedAt, workedMs);
  const list = Array.isArray(steps) ? steps.map((step) => String(step || '').trim()).filter(Boolean) : [];
  const canOpen = list.length > 0;
  const lined = Boolean(String(label || '').trim());

  if (statusOnComposer) {
    if (!lined) return null;
    return (
      <div className="chat-activity">
        <span className="chat-activity-label">{label}</span>
      </div>
    );
  }

  return (
    <div
      className={`chat-activity${lined ? ' chat-activity--with-label' : ''}${active ? ' is-live' : ' is-settled'}`}
      aria-live={lined ? undefined : 'polite'}
      aria-atomic={lined ? undefined : 'true'}
      aria-busy={active}
    >
      <span className="chat-activity-mark" aria-hidden="true" />
      {lined ? <span className="chat-activity-label">{label}</span> : null}
      <button
        type="button"
        className="chat-activity-phrase"
        aria-expanded={canOpen ? open : undefined}
        disabled={!canOpen}
        onClick={() => {
          if (canOpen) setOpen((value) => !value);
        }}
      >
        <span>{phrase}</span>
        {canOpen ? <span className={`chat-activity-chevron${open ? ' is-open' : ''}`} aria-hidden="true" /> : null}
      </button>
      {open && canOpen ? (
        <ul className="chat-activity-steps">
          {list.map((step, index) => (
            <li key={`${index}-${step}`}>{step}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
